import { BoardState, FunctionMap, NewInboxState } from '../../types';
import { FSAInterface } from '../actions';
import {
  CREATE_BOARD_REQUEST,
  CREATE_BOARD_SUCCESS,
  NEW_INBOX_SET_ASSOCIATED_FBPAGE_REQUEST,
  NEW_INBOX_SET_ASSOCIATED_FBPAGE_SUCCESS,
  NEW_INBOX_SET_FB_TOKEN_SUCCESS,
  NEW_INBOX_SET_SUBSCRIBE_INSTAGRAM_MENTIONS_REQUEST,
  NEW_INBOX_CLEAR_SOCIAL_TOKEN_REQUEST,
  NEW_INBOX_CLEAR_SOCIAL_TOKEN_SUCCESS,
  NEW_INBOX_SET_TWITTER_ACC_TOKEN_SUCCESS,
  NEW_INBOX_SET_YOUTUBE_ACC_TOKEN_SUCCESS,
  NEW_INBOX_NEXT_STEP,
  NEW_INBOX_PREV_STEP,
  NEW_INBOX_ADD_KEYWORD_REQUEST,
  NEW_INBOX_ADD_KEYWORD_SUCCESS,
  NEW_INBOX_CLEAR,
  NEW_INBOX_REMOVE_KEYWORD_REQUEST,
  SET_BOARD,
} from '../constants';
import { createReducer } from './Utils';
import set from 'lodash/fp/set';
import get from 'lodash/fp/get';
import Keyword from '../../models/Keyword';

const initialState = {
  inbox: {
    keywords: [],
    members: [],
  },
  step: 1,
};

function setSocialToken(state: NewInboxState, action: FSAInterface<BoardState>) {
  return set('inbox.socialNetworks', action?.payload?.socialNetworks, state);
}

function nextStep(state: NewInboxState) {
  return { ...state, step: state.step + 1 };
}

function previousStep(state: NewInboxState) {
  return { ...state, step: state.step - 1 };
}

function inboxCreated(state: NewInboxState, action: FSAInterface<{ inbox: BoardState }>) {
  return { ...state, inbox: { ...state.inbox, ...action?.payload }, step: 2 };
}

function setAssociatedFBPageOptimistic(state: NewInboxState, action: FSAInterface<{ pageId: string }>) {
  return set('inbox.socialNetworks.facebook.associatedPage', action?.payload?.pageId, state);
}

function setAssociatedFBPage(state: NewInboxState, action: FSAInterface<{}>) {
  const { socialNetworks } = state?.inbox;
  if (!socialNetworks) {
    return state;
  }

  const facebook = { ...(socialNetworks.facebook || {}) };
  facebook.associatedPage = get('payload.socialNetworks.facebook.associatedPage', action);
  facebook.instagram = get('payload.socialNetworks.facebook.instagram', action);
  facebook.subscribedInstagramMentions = get('payload.socialNetworks.facebook.subscribedInstagramMentions', action);
  return { ...state, inbox: { ...state?.inbox, socialNetworks: { ...socialNetworks, facebook } } };
}

function setEntitySubscribedInstagramMentions(state: NewInboxState, action: FSAInterface<{}>) {
  const { socialNetworks } = state?.inbox;
  if (!socialNetworks) {
    return state;
  }

  const facebook = { ...(socialNetworks.facebook || {}) };
  facebook.subscribedInstagramMentions = get('payload.subscribe', action);
  return { ...state, inbox: { ...state?.inbox, socialNetworks: { ...socialNetworks, facebook } } };
}

function clearSocialToken(state: NewInboxState, action: FSAInterface<{ network: string }>) {
  const socialNetworks = { ...state?.inbox?.socialNetworks };

  if (action.payload) {
    socialNetworks[action.payload.network] = undefined;
  }

  return { ...state, inbox: { ...state.inbox, socialNetworks } };
}

function addKeywordOptimistic(state: NewInboxState, action: FSAInterface<{ keyword: string }>) {
  const keywords = [...state?.inbox?.keywords, { keyword: action.payload?.keyword }];
  return set('inbox.keywords', keywords, state);
}

function addKeyword(state: NewInboxState, action: FSAInterface<Keyword>) {
  const keywords = state?.inbox?.keywords.map((keyword: Keyword) => {
    if (action?.payload?._id && !keyword._id) {
      return action.payload;
    }
    return keyword;
  });
  const updatedKeywords = action?.payload?._id ? keywords : [...state?.inbox?.keywords, action.payload];
  return { ...state, inbox: { ...state.inbox, keywords: updatedKeywords } };
}

function removeKeyword(state: NewInboxState, action: FSAInterface<{ keyword: Keyword }>) {
  const keywords = state.inbox.keywords.filter((keyword) => keyword._id !== action?.payload?.keyword._id);
  return { ...state, inbox: { ...state.inbox, keywords } };
}

function clearNewInbox() {
  return initialState;
}

function setBoard(state: NewInboxState, action: FSAInterface<BoardState>) {
  return { ...state, inbox: { ...state.inbox, ...action?.payload } };
}

const functionMap: FunctionMap = {
  [NEW_INBOX_SET_FB_TOKEN_SUCCESS]: setSocialToken,
  [NEW_INBOX_SET_ASSOCIATED_FBPAGE_REQUEST]: setAssociatedFBPageOptimistic,
  [NEW_INBOX_SET_ASSOCIATED_FBPAGE_SUCCESS]: setAssociatedFBPage,
  [NEW_INBOX_SET_SUBSCRIBE_INSTAGRAM_MENTIONS_REQUEST]: setEntitySubscribedInstagramMentions,
  [CREATE_BOARD_REQUEST]: nextStep,
  [CREATE_BOARD_SUCCESS]: inboxCreated,
  [SET_BOARD]: setBoard,
  [NEW_INBOX_CLEAR_SOCIAL_TOKEN_REQUEST]: clearSocialToken,
  [NEW_INBOX_CLEAR_SOCIAL_TOKEN_SUCCESS]: clearSocialToken,
  [NEW_INBOX_SET_TWITTER_ACC_TOKEN_SUCCESS]: setSocialToken,
  [NEW_INBOX_SET_YOUTUBE_ACC_TOKEN_SUCCESS]: setSocialToken,
  [NEW_INBOX_NEXT_STEP]: nextStep,
  [NEW_INBOX_PREV_STEP]: previousStep,
  [NEW_INBOX_ADD_KEYWORD_REQUEST]: addKeywordOptimistic,
  [NEW_INBOX_ADD_KEYWORD_SUCCESS]: addKeyword,
  [NEW_INBOX_REMOVE_KEYWORD_REQUEST]: removeKeyword,
  [NEW_INBOX_CLEAR]: clearNewInbox,
};

export default createReducer(functionMap, initialState);
